import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildUrl, ENDPOINTS } from '../config';
import useFieldConfig from '../hooks/useFieldConfig';
import useDebounce from '../hooks/useDebounce';
import LoadingSpinner from '../components/LoadingSpinner';
import './HomePage.css';

function HomePage() {
    const navigate = useNavigate();
    const { loading: fieldsLoading, getListFields } = useFieldConfig();
    const [elevators, setElevators] = useState([]);
    const [filteredElevators, setFilteredElevators] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        loadElevators();
    }, []);

    useEffect(() => {
        if (debouncedSearchTerm.trim()) {
            const keyword = debouncedSearchTerm.toLowerCase();
            const nextElevators = elevators.filter((elevator) =>
                Object.values(elevator).some((value) =>
                    value && String(value).toLowerCase().includes(keyword)
                )
            );
            setFilteredElevators(nextElevators);
            return;
        }

        setFilteredElevators(elevators);
    }, [debouncedSearchTerm, elevators]);

    const loadElevators = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(buildUrl(ENDPOINTS.elevators));
            setElevators(response.data);
            setFilteredElevators(response.data);
        } catch (requestError) {
            console.error('加载电梯列表失败:', requestError);
            setError('暂时无法连接到服务器，请稍后重试。');
        } finally {
            setLoading(false);
        }
    };

    const listFields = getListFields().filter((field) => field.name !== 'id');

    const populatedFieldCount = (elevator) =>
        listFields.filter((field) => elevator[field.name]).length;

    const getLeadField = (elevator) =>
        listFields.find((field) => elevator[field.name]);

    if (fieldsLoading || loading) {
        return <LoadingSpinner message="正在加载电梯档案..." />;
    }

    if (error) {
        return (
            <div className="page-shell">
                <div className="page-container home-page">
                    <div className="app-panel app-message-card home-message-card">
                        <span className="app-eyebrow">系统提示</span>
                        <h2>{error}</h2>
                        <p>你可以稍后再试，或刷新列表重新发起请求。</p>
                        <div className="home-message-card__actions">
                            <button type="button" className="app-button app-button--primary" onClick={loadElevators}>
                                重新加载
                            </button>
                            <button
                                type="button"
                                className="app-button app-button--secondary"
                                onClick={() => navigate('/admin')}
                            >
                                进入管理端
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <div className="page-container home-page">
                <section className="home-hero app-panel">
                    <div className="home-hero__copy">
                        <span className="app-eyebrow">96333 电梯信息查询</span>
                        <h1>蓝海 96333 电梯档案中心</h1>
                        <p>
                            支持按电梯编号、地址、使用单位等关键词快速检索，也可以扫码直接打开对应详情页。
                        </p>
                    </div>

                    <div className="app-kpi-grid home-hero__stats">
                        <div className="app-kpi-card">
                            <strong>{elevators.length}</strong>
                            <span>在册电梯</span>
                        </div>
                        <div className="app-kpi-card">
                            <strong>{filteredElevators.length}</strong>
                            <span>当前结果</span>
                        </div>
                        <div className="app-kpi-card">
                            <strong>{listFields.length}</strong>
                            <span>展示字段</span>
                        </div>
                    </div>
                </section>

                <section className="home-toolbar app-panel app-panel--strong">
                    <div className="page-heading home-toolbar__heading">
                        <div>
                            <span className="app-eyebrow">快速检索</span>
                            <h2>查找具体电梯</h2>
                            <p>输入任意已登记字段即可筛选，结果区域会实时更新。</p>
                        </div>
                        <div className="home-toolbar__actions">
                            <button type="button" className="app-button app-button--secondary" onClick={loadElevators}>
                                刷新列表
                            </button>
                            <button
                                type="button"
                                className="app-button app-button--primary"
                                onClick={() => navigate('/admin')}
                            >
                                管理员入口
                            </button>
                        </div>
                    </div>

                    <div className="home-search">
                        <label className="home-search__field">
                            <span className="home-search__label">关键词搜索</span>
                            <div className="home-search__input">
                                <input
                                    type="text"
                                    className="app-input"
                                    placeholder="例如：电梯编号、安装地址、使用单位"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="app-button app-button--ghost app-button--small home-search__clear"
                                        onClick={() => setSearchTerm('')}
                                        aria-label="清除搜索词"
                                    >
                                        清除
                                    </button>
                                )}
                            </div>
                        </label>
                    </div>
                </section>

                <section className="home-results">
                    <div className="home-results__header">
                        <div>
                            <h2>检索结果</h2>
                            <p>
                                {searchTerm.trim()
                                    ? `关键词“${searchTerm.trim()}”共匹配 ${filteredElevators.length} 条记录。`
                                    : '按登记字段展示全部电梯，点击卡片可进入详情页。'}
                            </p>
                        </div>
                        <div className="home-results__badges">
                            <span className="app-badge">{filteredElevators.length} 条结果</span>
                            {searchTerm.trim() && <span className="app-badge app-badge--accent">已启用筛选</span>}
                        </div>
                    </div>

                    {filteredElevators.length > 0 ? (
                        <div className="home-results__grid">
                            {filteredElevators.map((elevator) => {
                                const leadField = getLeadField(elevator);
                                const elevatorFields = listFields.filter((field) => elevator[field.name]);

                                return (
                                    <article
                                        key={elevator.id}
                                        className="home-card app-panel app-panel--strong"
                                        onClick={() => navigate(`/elevator/${elevator.id}`)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                navigate(`/elevator/${elevator.id}`);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="home-card__header">
                                            <div>
                                                <span className="home-card__id-label">电梯编号</span>
                                                <h3>{elevator.id}</h3>
                                                <p>{leadField ? `${leadField.label}：${leadField.value}` : '点击查看完整档案信息'}</p>
                                            </div>
                                            <span className="app-badge app-badge--accent">查看详情</span>
                                        </div>

                                        <dl className="home-card__details">
                                            {elevatorFields.length > 0 ? (
                                                elevatorFields.map((field) => (
                                                    <div key={field.name} className="home-card__detail">
                                                        <dt>{field.label}</dt>
                                                        <dd>{elevator[field.name]}</dd>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="home-card__detail home-card__detail--empty">
                                                    <dt>暂无公开字段</dt>
                                                    <dd>进入详情页后查看完整登记信息。</dd>
                                                </div>
                                            )}
                                        </dl>

                                        <div className="home-card__footer">
                                            <span>{populatedFieldCount(elevator)} 项已展示信息</span>
                                            <span>点击进入详情</span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="app-panel app-empty-state home-empty-state">
                            <span className="app-eyebrow">没有匹配结果</span>
                            <h3>未找到符合条件的电梯</h3>
                            <p>可以换一个关键词，或者先清除筛选后查看全部记录。</p>
                            <div className="home-empty-state__actions">
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="app-button app-button--primary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        清除筛选
                                    </button>
                                )}
                                <button type="button" className="app-button app-button--secondary" onClick={loadElevators}>
                                    重新加载
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

export default HomePage;
